import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../context/CompanyContext';
import api from '../../services/api';
import './AccountingEntries.css';

const AccountingEntries = () => {
  const { companyId } = useCompany();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [, setSelectedEntry] = useState(null);
  const [formData, setFormData] = useState({
    entryDate: new Date().toISOString().split('T')[0],
    entryType: 'diario',
    description: '',
    lines: [
      { accountId: '', description: '', debit: 0, credit: 0 },
      { accountId: '', description: '', debit: 0, credit: 0 }
    ]
  });

  // Obtener comprobantes
  const { data, isLoading, error } = useQuery({
    queryKey: ['accountingEntries', companyId],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/accounting-entries`);
      return response.data.data;
    },
    enabled: !!companyId
  });

  // Obtener plan de cuentas para el selector
  const { data: chartOfAccounts } = useQuery({
    queryKey: ['chartOfAccounts', companyId],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/chart-of-accounts`);
      return response.data.data.flat || [];
    },
    enabled: !!companyId
  });

  // Crear comprobante
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post(`/companies/${companyId}/accounting-entries`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accountingEntries', companyId]);
      setShowForm(false);
      setFormData({
        entryDate: new Date().toISOString().split('T')[0],
        entryType: 'diario',
        description: '',
        lines: [
          { accountId: '', description: '', debit: 0, credit: 0 },
          { accountId: '', description: '', debit: 0, credit: 0 }
        ]
      });
    }
  });

  // Aprobar comprobante
  const approveMutation = useMutation({
    mutationFn: async (entryId) => {
      const response = await api.post(`/companies/${companyId}/accounting-entries/${entryId}/approve`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accountingEntries', companyId]);
    }
  });

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { accountId: '', description: '', debit: 0, credit: 0 }]
    });
  };

  const removeLine = (index) => {
    if (formData.lines.length > 2) {
      setFormData({
        ...formData,
        lines: formData.lines.filter((_, i) => i !== index)
      });
    }
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    
    // Si se ingresa débito, limpiar crédito y viceversa
    if (field === 'debit' && parseFloat(value) > 0) {
      newLines[index].credit = 0;
    }
    if (field === 'credit' && parseFloat(value) > 0) {
      newLines[index].debit = 0;
    }
    
    setFormData({ ...formData, lines: newLines });
  };

  const calculateTotals = () => {
    const totalDebit = formData.lines.reduce((sum, line) => sum + parseFloat(line.debit || 0), 0);
    const totalCredit = formData.lines.reduce((sum, line) => sum + parseFloat(line.credit || 0), 0);
    return { totalDebit, totalCredit, difference: Math.abs(totalDebit - totalCredit) };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const totals = calculateTotals();
    
    if (totals.difference > 0.01) {
      alert(`El comprobante no está balanceado. Diferencia: ${totals.difference.toFixed(2)}`);
      return;
    }

    createMutation.mutate({
      entryDate: formData.entryDate,
      entryType: formData.entryType,
      description: formData.description,
      lines: formData.lines.map(line => ({
        accountId: line.accountId,
        description: line.description,
        debit: parseFloat(line.debit || 0),
        credit: parseFloat(line.credit || 0)
      }))
    });
  };

  const totals = calculateTotals();
  const isBalanced = totals.difference <= 0.01;

  if (isLoading) return <div>Cargando comprobantes...</div>;
  if (error) return <div>Error al cargar comprobantes</div>;

  return (
    <div className="accounting-entries">
      <div className="page-header">
        <h2>Comprobantes Contables</h2>
        <button 
          className="btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancelar' : '+ Nuevo Comprobante'}
        </button>
      </div>

      {showForm && (
        <div className="entry-form-card">
          <h3>Nuevo Comprobante</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Fecha</label>
                <input
                  type="date"
                  value={formData.entryDate}
                  onChange={(e) => setFormData({ ...formData, entryDate: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Tipo</label>
                <select
                  value={formData.entryType}
                  onChange={(e) => setFormData({ ...formData, entryType: e.target.value })}
                  required
                >
                  <option value="diario">Diario</option>
                  <option value="ajuste">Ajuste</option>
                  <option value="cierre">Cierre</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Descripción</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows="3"
              />
            </div>

            <div className="lines-section">
              <div className="lines-header">
                <h4>Líneas del Comprobante</h4>
                <button type="button" onClick={addLine} className="btn-secondary">
                  + Agregar Línea
                </button>
              </div>

              <table className="lines-table">
                <thead>
                  <tr>
                    <th>Cuenta</th>
                    <th>Descripción</th>
                    <th>Débito</th>
                    <th>Crédito</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.lines.map((line, index) => (
                    <tr key={index}>
                      <td>
                        <select
                          value={line.accountId}
                          onChange={(e) => updateLine(index, 'accountId', e.target.value)}
                          required
                        >
                          <option value="">Seleccionar...</option>
                          {chartOfAccounts?.filter(acc => acc.allowsMovements).map(acc => (
                            <option key={acc.id} value={acc.id}>
                              {acc.code} - {acc.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={line.description}
                          onChange={(e) => updateLine(index, 'description', e.target.value)}
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={line.debit}
                          onChange={(e) => updateLine(index, 'debit', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={line.credit}
                          onChange={(e) => updateLine(index, 'credit', e.target.value)}
                        />
                      </td>
                      <td>
                        {formData.lines.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeLine(index)}
                            className="btn-danger"
                          >
                            ×
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="totals-row">
                    <td colSpan="2"><strong>Totales</strong></td>
                    <td><strong>{totals.totalDebit.toFixed(2)}</strong></td>
                    <td><strong>{totals.totalCredit.toFixed(2)}</strong></td>
                    <td>
                      {isBalanced ? (
                        <span className="balance-ok">✓ Balanceado</span>
                      ) : (
                        <span className="balance-error">
                          Diferencia: {totals.difference.toFixed(2)}
                        </span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn-primary" 
                disabled={createMutation.isLoading || !isBalanced}
              >
                {createMutation.isLoading ? 'Creando...' : 'Crear Comprobante'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="entries-list">
        <h3>Lista de Comprobantes</h3>
        {data?.entries && data.entries.length > 0 ? (
          <table className="entries-table">
            <thead>
              <tr>
                <th>Número</th>
                <th>Fecha</th>
                <th>Descripción</th>
                <th>Débito</th>
                <th>Crédito</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map(entry => (
                <tr key={entry.id}>
                  <td>{entry.entryNumber}</td>
                  <td>{new Date(entry.entryDate).toLocaleDateString()}</td>
                  <td>{entry.description}</td>
                  <td>{entry.totalDebit.toFixed(2)}</td>
                  <td>{entry.totalCredit.toFixed(2)}</td>
                  <td>
                    <span className={`status-badge status-${entry.status}`}>
                      {entry.status}
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={() => setSelectedEntry(entry)}
                      className="btn-link"
                    >
                      Ver
                    </button>
                    {entry.status === 'borrador' && (
                      <button
                        onClick={() => approveMutation.mutate(entry.id)}
                        className="btn-link"
                        disabled={approveMutation.isLoading}
                      >
                        Aprobar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <p>No hay comprobantes registrados</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountingEntries;

